/**
 * Block 样式计算工具函数
 * 复刻 trial_demo_editor 的渲染方式，供原始版和编辑版共用
 */

import type { BlockStyleConfig } from './types';
import type { ComputedLevelStyle } from '../../../schema/core/types';
import { twipsToPx, halfPointsToPx, lineSpacingToCSS } from '../../../schema/utils/units';
import type { NoteBlock } from '../../../types';

// ============ Word A4 内容宽度常量 ============

/** A4 页面宽度 11906 twips，左右边距各 1440 twips，内容宽度 = 9026 twips */
const CONTENT_WIDTH_TWIPS = 11906 - 1440 * 2; // 9026
/** 前端内容区宽度（px），与 Word 导出严格一致 */
export const WORD_CONTENT_WIDTH_PX = Math.round(twipsToPx(CONTENT_WIDTH_TWIPS)); // 602

// ============ 编号格式化 ============

const toChineseNumber = (n: number): string => {
  const nums = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十',
    '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十'];
  if (n <= 20) return nums[n];
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    if (tens === 1) return ones ? `十${nums[ones]}` : '十';
    return `${nums[tens]}十${ones ? nums[ones] : ''}`;
  }
  return String(n);
};

const toRoman = (n: number): string => {
  const romans = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
    'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
  return romans[n] || String(n);
};

const toLowerRoman = (n: number): string => toRoman(n).toLowerCase();

const toLetter = (n: number, upper: boolean): string => {
  if (n >= 1 && n <= 26) {
    const code = upper ? 64 + n : 96 + n;
    return String.fromCharCode(code);
  }
  return String(n);
};

function generateFontFamily(font: { ascii?: string | null; eastAsia?: string | null; hAnsi?: string | null }): string {
  const families: string[] = [];
  if (font.ascii) families.push(font.ascii.includes(' ') ? `"${font.ascii}"` : font.ascii);
  if (font.eastAsia) families.push(font.eastAsia.includes(' ') ? `"${font.eastAsia}"` : font.eastAsia);
  if (font.hAnsi && font.hAnsi !== font.ascii) families.push(font.hAnsi.includes(' ') ? `"${font.hAnsi}"` : font.hAnsi);
  families.push('system-ui', 'sans-serif');
  return families.join(', ');
}

// ============ 编号格式化（通用） ============

function formatNumber(n: number, numFmt: string): string {
  if (numFmt === 'chineseCountingThousand') return toChineseNumber(n);
  if (numFmt === 'upperRoman') return toRoman(n);
  if (numFmt === 'lowerRoman') return toLowerRoman(n);
  if (numFmt === 'upperLetter') return toLetter(n, true);
  if (numFmt === 'lowerLetter') return toLetter(n, false);
  return String(n);
}

// ============ bulletText 计算 ============

/**
 * 计算编号文本
 * @param computed - 当前级别的计算样式
 * @param levelIndex - 当前级别在同级中的 0-based 索引
 * @param counters - 各级别的当前计数值（1-based），用于多级 pattern 如 %1.%2
 * @param allLevelStyles - 所有级别的样式（用于获取被引用级别的 numFmt）
 */
export function computeBulletText(
  computed: ComputedLevelStyle,
  levelIndex: number,
  counters?: number[],
  allLevelStyles?: ComputedLevelStyle[],
): string {
  if (!computed.pattern) return '';

  if (computed.numFmt === 'bullet') {
    return computed.pattern || '●';
  }

  let text = computed.pattern;
  text = text.replace(/%(\d)/g, (_match, levelRef) => {
    const refLevel = parseInt(levelRef, 10) - 1; // %1 → level 0
    if (counters && counters[refLevel] !== undefined && counters[refLevel] > 0) {
      const refCount = counters[refLevel];
      // Use the referenced level's numFmt if available, otherwise fall back to current
      const refNumFmt = allLevelStyles?.[refLevel]?.numFmt || computed.numFmt;
      return formatNumber(refCount, refNumFmt);
    }
    // Fallback: use current level index
    return formatNumber(levelIndex + 1, computed.numFmt);
  });
  return text;
}

// ============ styleConfig 计算 ============

export function computeStyleConfig(computed: ComputedLevelStyle): BlockStyleConfig {
  // 限制缩进不超出内容区域，与 Word 导出一致
  const maxIndentPx = WORD_CONTENT_WIDTH_PX - 20; // 至少留 20px 给文本
  const paddingLeftPx = Math.min(twipsToPx(computed.indent.left), maxIndentPx);
  let hangingPx = twipsToPx(computed.indent.hanging);
  const hasPattern = !!computed.pattern;
  const isNoneType = !hasPattern;

  // 与 Word 导出一致：isNoneType 级别 hanging=0，文本从 left 位置开始
  if (isNoneType) {
    hangingPx = 0;
  } else {
    // 有 pattern 时，确保最小 hanging 为 20px（给编号留空间）
    if (hangingPx < 20) {
      hangingPx = 20;
    }
  }

  const selectionStartPx = paddingLeftPx - hangingPx;

  const fontFamily = computed.font ? generateFontFamily(computed.font) : undefined;
  const fontSize = computed.fontSize ? `${halfPointsToPx(computed.fontSize).toFixed(2)}px` : undefined;
  const fontWeight = computed.bold ? 700 : undefined;
  const fontStyle = computed.italic ? 'italic' as const : undefined;

  // Word auto 行距计算：lineValue/240 是倍数，但 Word 基于字体 metrics（ascent+descent）
  // 而非 CSS font-size。CJK 字体（如微软雅黑）的 metrics 比 font-size 大约 1.2 倍。
  // CSS line-height 直接基于 font-size，所以需要补偿这个差异。
  let lineHeight: number | string = 1.5;
  if (computed.spacing?.line) {
    const lineValue = parseInt(computed.spacing.line, 10);
    const rule = computed.spacing.lineRule;
    if (rule === 'exact' || rule === 'atLeast') {
      lineHeight = lineSpacingToCSS(lineValue, rule);
    } else {
      // auto 模式：Word 的行距基于字体 metrics，CSS 需要乘以修正系数
      const wordMultiplier = lineValue / 240;
      lineHeight = wordMultiplier * 1.2;
    }
  }
  const lineHeightNum = typeof lineHeight === 'number' ? lineHeight : 1.5;
  const trimEm = (lineHeightNum - 1) / 2 * -1;

  // Word 段落间距：spacing.before/after 单位是 twips
  // Word 不做 margin collapse，before 和 after 是叠加的
  // 用 paddingTop/paddingBottom 模拟，避免 CSS margin collapse
  const paddingTop = computed.spacing?.before
    ? `${twipsToPx(parseInt(computed.spacing.before, 10)).toFixed(2)}px`
    : '0px';
  const paddingBottom = computed.spacing?.after
    ? `${twipsToPx(parseInt(computed.spacing.after, 10)).toFixed(2)}px`
    : '0px';

  return {
    outerContainer: {
      position: 'relative',
      display: 'block',
      width: '100%',
      paddingTop,
      paddingBottom,
      cursor: 'text',
    },
    contentWrapper: {
      position: 'relative',
      marginLeft: `${Math.max(0, selectionStartPx)}px`,
      paddingLeft: '0px',
      cursor: 'text',
    },
    gutter: {
      display: 'none',
    },
    selectionWrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      width: '100%',
      borderRadius: '4px',
      transition: 'all 0.15s ease',
      boxSizing: 'border-box',
    },
    bullet: {
      width: `${hangingPx}px`,
      flexShrink: 0,
      fontFamily,
      fontSize,
      fontWeight,
      fontStyle,
      userSelect: 'none',
      lineHeight,
    },
    content: {
      flexGrow: 1,
      fontSize,
      fontWeight,
      fontFamily,
      fontStyle,
      lineHeight,
      outline: 'none',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      textDecoration: computed.underline ? 'underline' : undefined,
    },
  };
}

/**
 * 编辑版 styleConfig
 * 内容区样式与原始版完全一致（与 Word 导出一致）
 * 拖拽按钮由外层布局单独处理，不影响内容区
 */
export function computeEditableStyleConfig(computed: ComputedLevelStyle): BlockStyleConfig {
  return computeStyleConfig(computed);
}

// ============ 编号索引计算 ============

export function computeBlockLevelIndices(blocks: NoteBlock[]): { index: number; counters: number[] }[] {
  const results: { index: number; counters: number[] }[] = [];
  const counters: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const lvl = block.level;
    for (let j = lvl; j < counters.length; j++) {
      counters[j] = 0;
    }
    counters[lvl - 1]++;
    results.push({ index: counters[lvl - 1] - 1, counters: [...counters] });
  }
  return results;
}
